import React, { useState, useEffect } from "react";
import { Plus, Save } from "lucide-react";
import { podcastsApi } from "../../utils/api";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";

const PodcastManagement = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPodcast, setEditingPodcast] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    intro: "",
    outro: "",
    script_model: "gpt-4o-mini",
  });

  const SCRIPT_MODELS = [
    { value: "gpt-4o-mini", label: "GPT-4o Mini" },
    { value: "gpt-4o", label: "GPT-4o" },
    { value: "gpt-4.1", label: "GPT-4.1" },
    { value: "gpt-5-mini", label: "GPT-5 Mini" },
    { value: "gpt-5.1", label: "GPT-5.1" },
    { value: "gpt-5.2", label: "GPT-5.2" },
  ];

  useEffect(() => {
    fetchPodcasts();
  }, []);

  const fetchPodcasts = async () => {
    setLoading(true);
    try {
      const data = await podcastsApi.getAll();
      setPodcasts(data);
    } catch (error) {
      console.error("Error fetching podcasts:", error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingPodcast) {
        await podcastsApi.update(editingPodcast.id, formData);
      } else {
        await podcastsApi.create(formData);
      }
      fetchPodcasts();
      resetForm();
    } catch (error) {
      console.error("Error saving podcast:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this podcast?")) return;
    setLoading(true);
    try {
      await podcastsApi.delete(id);
      fetchPodcasts();
    } catch (error) {
      console.error("Error deleting podcast:", error);
    }
    setLoading(false);
  };

  const handleEdit = (podcast) => {
    setEditingPodcast(podcast);
    setFormData({
      title: podcast.title || "",
      intro: podcast.intro || "",
      outro: podcast.outro || "",
      script_model: podcast.script_model || "gpt-4o-mini",
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      intro: "",
      outro: "",
      script_model: "gpt-4o-mini",
    });
    setEditingPodcast(null);
    setShowForm(false);
  };

  const filteredPodcasts = podcasts.filter((podcast) =>
    podcast.title?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchPodcasts}
          placeholder="Search podcasts..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Podcast
        </PrimaryButton>
      </div>

      <FormModal
        title={editingPodcast ? "Edit Podcast" : "New Podcast"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Intro
            </label>
            <textarea
              value={formData.intro}
              onChange={(e) =>
                setFormData({ ...formData, intro: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Outro
            </label>
            <textarea
              value={formData.outro}
              onChange={(e) =>
                setFormData({ ...formData, outro: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Script Model
            </label>
            <select
              value={formData.script_model}
              onChange={(e) =>
                setFormData({ ...formData, script_model: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {SCRIPT_MODELS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              GPT-4o produces longer, better-structured scripts but costs ~15×
              more per episode.
            </p>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Podcast"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Intro
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Outro
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Model
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredPodcasts.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No podcasts found. Create one to get started!
                </td>
              </tr>
            ) : (
              filteredPodcasts.map((podcast) => (
                <tr key={podcast.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {podcast.title}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                    {podcast.intro || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                    {podcast.outro || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-mono">
                    {podcast.script_model || "gpt-4o-mini"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    <ActionButtons
                      onEdit={() => handleEdit(podcast)}
                      onDelete={() => handleDelete(podcast.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PodcastManagement;
